import { authAdapter } from '@/app/_auth/auth'
import { useAuth } from '@/app/_auth/auth-hooks'
import { AuthStep } from '@/app/_auth/auth-step-navigator'
import {
  AUTH_PROVIDER_DATA,
  AuthProviderID,
  LoginButton,
} from '@/app/_auth/steps/_common/login-buttons'
import { toast } from '@/components/ui/toaster'
import { cn } from '@/lib/utils'
import {
  type ParamsTags,
  T,
  type TranslateParams,
  type TranslationKey,
  useTranslate,
} from '@tolgee/react'
import React from 'react'
import { AuthHeader } from './auth-header'

const EmergenceLogo = React.lazy(() =>
  import('@/app/_common/emergence-logo').then((m) => ({ default: m.EmergenceLogo })),
)

export const AuthStepTemplate = ({
  children,
  noContainerStyles,
  hideLogo,
}: ComponentProps<{ noContainerStyles?: boolean; hideLogo?: boolean }>) => (
  <main className='relative flex h-dvh w-full items-center justify-center bg-neutral-50 font-circular-xx'>
    <AuthHeader className='absolute top-0' />
    <section
      className={cn({
        'flex w-full max-w-lg flex-col items-center gap-8 rounded-2xl border border-white bg-neutral-white px-4 py-14 shadow-sidebar xs:px-14':
          !noContainerStyles,
      })}
    >
      {!hideLogo && <EmergenceLogo className='w-40 p-0' />}
      {children}
    </section>
  </main>
)

// 👇 text

type TextProps = {
  className?: string
  keyName: TranslationKey
  params?: TranslateParams<ParamsTags>
}

AuthStepTemplate.H1 = function H1({ keyName, params, className }: TextProps) {
  return (
    <h1 className={cn('text-2xl font-light xs:text-3xl', className)}>
      <T keyName={keyName} params={params} />
    </h1>
  )
}

AuthStepTemplate.H2 = function H2({ keyName, params, className }: TextProps) {
  return (
    <h2 className={cn('text-2xl font-light', className)}>
      <T keyName={keyName} params={params} />
    </h2>
  )
}

AuthStepTemplate.H3 = function H3({ keyName, params, className }: TextProps) {
  return (
    <h3 className={cn('text-xl font-light', className)}>
      <T keyName={keyName} params={params} />
    </h3>
  )
}

AuthStepTemplate.P = function P({ keyName, params, className }: TextProps) {
  return (
    <p className={className}>
      <T keyName={keyName} params={params} />
    </p>
  )
}

AuthStepTemplate.RequiredFieldsMessage = function RequiredFieldsMessage() {
  return (
    <AuthStepTemplate.P
      keyName='auth.steps.common.requiredFields'
      className='text-xs font-light italic'
    />
  )
}

// 👇 containers

AuthStepTemplate.Header = function Header({ children, className }: ComponentProps) {
  return (
    <div className={cn('flex flex-col items-center gap-3 text-center', className)}>{children}</div>
  )
}

AuthStepTemplate.ButtonRow = function ButtonRow({ children, className }: ComponentProps) {
  return <div className={cn('flex w-full gap-6', className)}>{children}</div>
}

// 👇 footer

AuthStepTemplate.Footer = function AuthFooter({
  variant = 'signIn',
}: {
  variant?: 'signIn' | 'signUp'
}) {
  const { setStep } = useAuth()

  if (variant === 'signUp')
    return (
      <AuthStepTemplate.P
        keyName='auth.footer.signUp'
        params={{
          i: <span className='text-neutral-500' />,
          a: (
            <button
              onClick={() => setStep(AuthStep.Create)}
              className='font-bold text-grey-300 outline-fig-300'
            />
          ),
        }}
      />
    )

  return (
    <AuthStepTemplate.P
      keyName='auth.footer.signIn'
      params={{
        i: <span className='text-neutral-500' />,
        a: (
          <button
            onClick={() => setStep(AuthStep.Login)}
            className='font-bold text-grey-300 outline-fig-300'
          />
        ),
      }}
    />
  )
}

// 👇 provider buttons

AuthStepTemplate.LoginButtons = function LoginButtons({
  providers = [AuthProviderID.Google, AuthProviderID.Microsoft],
}: {
  providers?: AuthProviderID[]
}) {
  const { t } = useTranslate()
  const { step, setStep, setAuthError, authError } = useAuth()

  const login = async (providerId: AuthProviderID) => {
    if (!authAdapter) {
      console.error('Auth adapter not initialized')
      return
    }

    try {
      await authAdapter.signIn(providerId)

      // Handle Firebase-specific credential linking
      if (
        authAdapter.getCurrentUser() &&
        step === AuthStep.LinkProvider &&
        authError &&
        authError.code === 'auth/account-exists-with-different-credential' &&
        authAdapter.getCredentialFromError
      ) {
        const credential = authAdapter.getCredentialFromError(authError)
        if (credential && authAdapter.linkWithCredential) {
          await authAdapter.linkWithCredential(authAdapter.getCurrentUser()!, credential)
          setAuthError(null)
        }
      }
    } catch (error: unknown) {
      // Handle Firebase-specific errors
      const firebaseError = error as { code?: string; message?: string } | null | undefined
      const errorCode = firebaseError?.code
      const errorMessage = firebaseError?.message

      if (errorCode === 'auth/cancelled-popup-request') {
        return
      }

      if (errorCode === 'auth/account-exists-with-different-credential') {
        const email = (error as { customData?: { email?: string } })?.customData?.email
        if (email) {
          // Account exists with different credential
        }
        setAuthError(error as typeof authError)
        setStep(AuthStep.LinkProvider)
        return
      }

      if (typeof errorMessage === 'string' && errorMessage.includes('Unauthorized email')) {
        toast.error('Unauthorized email address. Please try again using a different email.', {
          position: 'bottom-right',
          duration: 10000,
        })
        return
      }

      // Handle generic errors (including Keycloak errors)
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Login failed. Please try again.'

      console.error('Login failed:', error)
      toast.error(message, {
        position: 'bottom-right',
        duration: 5000,
      })
    }
  }

  return (
    <div className='flex w-full flex-col gap-3'>
      {providers.map((providerId) => {
        const { translationKeys, label, icon } = AUTH_PROVIDER_DATA[providerId]
        return (
          <LoginButton
            key={providerId}
            onClick={() => login(providerId)}
            title={translationKeys ? t(translationKeys.a11y) : label}
          >
            {icon}
            {translationKeys ? <T keyName={translationKeys.title} /> : label}
          </LoginButton>
        )
      })}
    </div>
  )
}
